import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  CartContext,
  type CartContextValue,
  type CartItem,
} from './cart-context';

/**
 * Marketplace cart — app-wide state, marketplace-only UI.
 *
 * Mounted at the App root so the AppMenuBar cart icon and every `/market/*`
 * surface share one cart. It stores ONLY product ids + quantities: it
 * deliberately does NOT import the catalog (`products.json`), because this
 * provider is in the always-loaded bundle and the catalog is kept behind the
 * lazy Marketplace chunk (see `src/views/lazyViews.tsx`). Consumers resolve
 * ids → products with `MARKETPLACE_PRODUCTS` themselves.
 *
 * Persistence: localStorage, per browser, guest and signed-in alike — a cart
 * is a pre-purchase scratchpad, not server state. The checkout edge function
 * re-prices every line from its own catalog copy, so nothing stored here is
 * trusted for money.
 */

const STORAGE_KEY = 'tk.marketplace.cart.v1';
const MAX_QUANTITY = 99;

function readStorage(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const items: CartItem[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const { productId, quantity } = entry as Partial<CartItem>;
      if (typeof productId !== 'string' || !productId) continue;
      if (seen.has(productId)) continue;
      const qty = Math.min(
        MAX_QUANTITY,
        Math.max(1, Math.floor(Number(quantity) || 1)),
      );
      seen.add(productId);
      items.push({ productId, quantity: qty });
    }
    return items;
  } catch {
    return [];
  }
}

function writeStorage(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* private mode / quota — the in-memory cart still works */
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(readStorage);

  useEffect(() => {
    writeStorage(items);
  }, [items]);

  // Keep multiple tabs in sync — adding in one tab updates the badge in another.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setItems(readStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const add = useCallback((productId: string, quantity = 1) => {
    if (!productId || quantity <= 0) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === productId);
      if (!existing) {
        return [
          ...prev,
          { productId, quantity: Math.min(MAX_QUANTITY, quantity) },
        ];
      }
      return prev.map((i) =>
        i.productId === productId
          ? { ...i, quantity: Math.min(MAX_QUANTITY, i.quantity + quantity) }
          : i,
      );
    });
  }, []);

  const remove = useCallback((productId: string) => {
    setItems((prev) => prev.filter((i) => i.productId !== productId));
  }, []);

  const setQuantity = useCallback((productId: string, quantity: number) => {
    setItems((prev) => {
      if (quantity <= 0) return prev.filter((i) => i.productId !== productId);
      const qty = Math.min(MAX_QUANTITY, Math.floor(quantity));
      if (!prev.some((i) => i.productId === productId)) {
        return [...prev, { productId, quantity: qty }];
      }
      return prev.map((i) =>
        i.productId === productId ? { ...i, quantity: qty } : i,
      );
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      count: items.reduce((n, i) => n + i.quantity, 0),
      add,
      remove,
      setQuantity,
      clear,
      has: (productId) => items.some((i) => i.productId === productId),
    }),
    [items, add, remove, setQuantity, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
